import React, { useState, useContext, createContext, useEffect } from "react";
import { Menu, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../../ui/sheet";

/* ────────────────────────────────────────────────────
   ADMIN CONTEXT  — sub-navigation key shared between
   sidebar, breadcrumb trail, and section components
──────────────────────────────────────────────────── */
const AdminCtx = createContext(null);
export const useAdminCtx = () => useContext(AdminCtx);

/** @deprecated use useAdminCtx */
export const useBreadcrumb = () => {
  const ctx = useContext(AdminCtx);
  if (!ctx) return null;
  // shim for old components that called setSubTrail([{label}])
  return { subTrail: [], setSubTrail: () => {} };
};

export const AdminProvider = ({ activeSection, children }) => {
  const [subKey, setSubKey] = useState(null);

  // Auto-reset sub-key whenever the top-level section changes
  useEffect(() => { setSubKey(null); }, [activeSection]);

  return (
    <AdminCtx.Provider value={{ subKey, setSubKey }}>
      {children}
    </AdminCtx.Provider>
  );
};

/* ────────────────────────────────────────────────────
   SUB-NAV definitions  (key must match component state)
──────────────────────────────────────────────────── */
export const SUB_NAV = {
  "Perspectives": [
    { key: "suggestions", label: "Suggestions" },
    { key: "draft",       label: "Drafts" },
    { key: "published",   label: "Published" },
    { key: "archived",    label: "Archived" },
    { key: "readers",     label: "Reader Proposals" },
    { key: "deleted",     label: "Deleted" },
  ],
  "Sources": [
    { key: "library",   label: "Source Library" },
    { key: "coverage",  label: "Country Coverage" },
    { key: "review",    label: "Discovery & Review" },
    { key: "history",   label: "Health & History" },
  ],
  "Content Economics": [
    { key: "coverage",   label: "Coverage" },
    { key: "spend",      label: "LLM Spend" },
    { key: "conversion", label: "Conversion Signals" },
  ],
};

/* ────────────────────────────────────────────────────
   PAGE DESCRIPTIONS — one per section
──────────────────────────────────────────────────── */
export const PAGE_META = {
  "Overview":           "Live pipeline snapshot — article counts, source errors, and last run status.",
  "Feed Health":        "Monitor RSS feed status per country and trigger manual fetches.",
  "Perspectives":       "Editorial topic curation — manage AI suggestions, drafts, and published briefs.",
  "Search Quality":     "Publisher coverage gaps, search-signal quality, and discovery run findings.",
  "Review Queue":       "Moderate flagged stories and reader-submitted corrections.",
  "Clusters":           "Browse and manage clustered news event groups from the ingestion pipeline.",
  "Sources":            "Source registry — RSS endpoints, organisation profiles, and country coverage.",
  "Topics":             "Feature, archive, or restore tracked news topics across all feeds.",
  "Users":              "Subscriber management, plan analytics, and CSV export.",
  "People":             "Administrator and editor accounts with platform access.",
  "Support":            "Reader support tickets and account help requests.",
  "Duplicate Reports":  "Review and resolve reader-submitted duplicate story reports.",
  "Content Economics":  "Coverage gaps, LLM spend analysis, and subscription conversion signals.",
  "Observability":      "LLM token usage, latency percentiles, and vendor spend breakdown.",
  "Data Pipeline":      "Source throughput, cost-per-article, and feed ingestion health.",
  "System Health":      "Service module status, incident log, and uptime overview.",
};

/* ────────────────────────────────────────────────────
   NAV GROUPS
──────────────────────────────────────────────────── */
const NAV_GROUPS = [
  { label: null,             items: ["Overview", "Feed Health"] },
  { label: "Editorial",      items: ["Perspectives", "Review Queue"] },
  { label: "Content",        items: ["Clusters", "Sources", "Topics"] },
  { label: "Analytics",      items: ["Search Quality", "Content Economics", "Duplicate Reports"] },
  { label: "Audience",       items: ["Users", "People", "Support"] },
  { label: "Infrastructure", items: ["Observability", "Data Pipeline", "System Health"] },
];

export const ADMIN_TABS = NAV_GROUPS.flatMap(g => g.items);

/* ────────────────────────────────────────────────────
   Shared nav list (used in sidebar + mobile drawer)
──────────────────────────────────────────────────── */
function SidebarNav({ active, onNavigate, onSubNavigate }) {
  const { subKey, setSubKey } = useAdminCtx() || {};

  const handleSubNav = (key) => {
    setSubKey?.(key);
    onSubNavigate?.();
  };

  return (
    <nav aria-label="Admin navigation" className="py-2">
      {NAV_GROUPS.map((group, gi) => (
        <div key={gi} className={gi > 0 ? "mt-1" : ""}>
          {group.label && (
            <p className="px-4 pt-4 pb-1 font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--muted)] select-none">
              {group.label}
            </p>
          )}
          {group.items.map(tab => {
            const subItems = SUB_NAV[tab] || [];
            const isActive = tab === active;
            const effectiveSubKey = isActive ? (subKey || subItems[0]?.key || null) : null;

            return (
              <div key={tab}>
                {/* Top-level section link */}
                <button
                  data-testid={`admin-tab-${tab.toLowerCase().replace(/\s+/g, "-")}`}
                  aria-current={isActive && !subItems.length ? "page" : undefined}
                  onClick={() => onNavigate(tab)}
                  className={`
                    w-full text-left pl-4 pr-3 py-2.5 text-[13px] leading-tight
                    transition-colors duration-150 border-l-2
                    ${isActive
                      ? "border-[var(--crimson)] text-[var(--ink)] font-medium bg-[var(--surface-2)]"
                      : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)]"
                    }
                  `}
                >
                  {tab}
                </button>

                {/* Sub-items — only visible when section is active */}
                {isActive && subItems.length > 0 && (
                  <div className="ml-4 border-l border-[var(--hairline)]">
                    {subItems.map(sub => (
                      <button
                        key={sub.key}
                        data-testid={`admin-sub-${sub.key}`}
                        aria-current={effectiveSubKey === sub.key ? "page" : undefined}
                        onClick={() => handleSubNav(sub.key)}
                        className={`
                          w-full text-left pl-3 pr-3 py-2 text-[12px] leading-tight
                          transition-colors duration-150
                          ${effectiveSubKey === sub.key
                            ? "text-[var(--crimson)] font-medium"
                            : "text-[var(--muted)] hover:text-[var(--ink)]"
                          }
                        `}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/* ────────────────────────────────────────────────────
   AdminSidebar — sticky desktop sidebar + mobile drawer
──────────────────────────────────────────────────── */
export const AdminSidebar = ({ active, setActive }) => {
  const [open, setOpen] = useState(false);

  const navigate = (value) => {
    setActive(value);
    window.scrollTo({ top: 0, behavior: "instant" });
    setOpen(false);
  };

  return (
    <>
      {/* ── Mobile bar ── */}
      <div
        data-testid="admin-mobile-bar"
        style={{ top: "var(--site-header-height)" }}
        className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-[var(--hairline)] bg-[var(--paper)] sticky z-30"
      >
        <button
          data-testid="admin-mobile-menu-btn"
          onClick={() => setOpen(true)}
          className="h-9 w-9 grid place-items-center rounded border border-[var(--hairline)] text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
          aria-label="Open admin navigation"
        >
          <Menu className="h-4 w-4" />
        </button>
        <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--muted)]">Admin</span>
        <span className="text-sm font-medium text-[var(--ink)] ml-1">{active}</span>
      </div>

      {/* ── Mobile Sheet Drawer ── */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-[var(--paper)] border-r border-[var(--hairline)]"
          data-testid="admin-mobile-drawer"
        >
          <div className="px-4 pt-6 pb-3 border-b border-[var(--hairline)]">
            <SheetTitle className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]">
              DNH · Administration
            </SheetTitle>
          </div>
          <div className="overflow-y-auto" style={{ height: "calc(100% - 56px)" }}>
            <SidebarNav
              active={active}
              onNavigate={navigate}
              onSubNavigate={() => setOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Desktop Sidebar ── */}
      <aside
        data-testid="admin-sidebar"
        style={{
          top: "var(--site-header-height)",
          height: "calc(100vh - var(--site-header-height))",
        }}
        className="hidden lg:flex flex-col w-[220px] xl:w-[240px] flex-shrink-0 border-r border-[var(--hairline)] bg-[var(--paper)] sticky overflow-y-auto"
      >
        <div className="px-4 pt-6 pb-3 border-b border-[var(--hairline)] flex-shrink-0">
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]">
            Administration
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SidebarNav
            active={active}
            onNavigate={v => { setActive(v); window.scrollTo({ top: 0, behavior: "instant" }); }}
          />
        </div>
      </aside>
    </>
  );
};

/* ────────────────────────────────────────────────────
   AdminHero — breadcrumbs + title + description
──────────────────────────────────────────────────── */
export const AdminHero = ({ active, onNavigate }) => {
  const { subKey } = useAdminCtx() || {};
  const subItems = SUB_NAV[active] || [];
  const activeSub = subItems.find(s => s.key === subKey) || subItems[0] || null;
  const description = PAGE_META[active];

  return (
    <header
      data-testid="admin-hero"
      className="px-6 lg:px-8 pt-7 pb-6 border-b border-[var(--hairline)]"
    >
      {/* Breadcrumb trail */}
      <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 mb-3">
        <button
          onClick={() => onNavigate?.("Overview")}
          className="font-mono text-[11px] text-[var(--muted)] hover:text-[var(--crimson)] transition-colors leading-none"
          data-testid="admin-breadcrumb-root"
        >
          Administration
        </button>

        <ChevronRight size={11} className="text-[var(--muted)] shrink-0" aria-hidden="true" />

        <span
          className={`font-mono text-[11px] leading-none ${!activeSub ? "text-[var(--ink)]" : "text-[var(--muted)]"}`}
          data-testid="admin-breadcrumb-section"
        >
          {active}
        </span>

        {activeSub && (
          <>
            <ChevronRight size={11} className="text-[var(--muted)] shrink-0" aria-hidden="true" />
            <span
              className="font-mono text-[11px] leading-none text-[var(--ink)]"
              data-testid="admin-breadcrumb-sub-0"
            >
              {activeSub.label}
            </span>
          </>
        )}
      </nav>

      {/* Page title */}
      <h1 data-testid="admin-page-title" className="font-serif text-4xl sm:text-5xl">
        {active}
      </h1>

      {/* Short description */}
      {description && (
        <p
          data-testid="admin-page-description"
          className="mt-2 text-sm text-[var(--muted)] max-w-2xl leading-relaxed"
        >
          {description}
        </p>
      )}
    </header>
  );
};
